import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, Pencil, Trash2, LogOut, ArrowLeft, ArrowUp, ArrowDown, X,
  ImagePlus, CreditCard, LayoutGrid, Inbox, MoveHorizontal, GripVertical, BarChart3, Settings, Link2, Send,
} from "lucide-react";
import { api, imgSrc, LOGO_URL, formatApiError } from "@/lib/api";
import { AnalyticsPanel } from "@/components/AnalyticsPanel";
import { SettingsPanel } from "@/components/SettingsPanel";
import { SmartImage } from "@/components/SmartImage";

const emptyApp = { name: "", tagline: "", description: "", main_image: "", images: [], featured: false, platforms: [], phone_frame: true, slug: "" };

const inputCls =
  "w-full bg-[#0C0C12] border border-[#282836] rounded-xl px-4 py-3 text-[#F4F4F8] placeholder:text-[#63637A] focus:outline-none focus:border-[#FF5500] transition-colors text-sm";

const UploadButton = ({ onUploaded, testid, label }) => {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        onUploaded(data.path);
      }
      toast.success(files.length > 1 ? `${files.length} images uploaded` : "Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label
      data-testid={testid}
      className={`inline-flex items-center gap-2 border border-dashed border-[#282836] hover:border-[#FF5500] text-[#9E9EB3] hover:text-white rounded-xl px-4 py-3 text-sm cursor-pointer transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
      {busy ? "Uploading..." : label}
      <input type="file" accept="image/*" multiple className="hidden" onChange={handle} />
    </label>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [tab, setTab] = useState("apps");
  const [apps, setApps] = useState([]);
  const [methods, setMethods] = useState([]);
  const [marquee, setMarquee] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  const [form, setForm] = useState(emptyApp);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pmForm, setPmForm] = useState({ label: "", logo: "" });
  const [pmEditingId, setPmEditingId] = useState(null);
  const [showPmForm, setShowPmForm] = useState(false);

  const [mqForm, setMqForm] = useState({ text: "" });
  const [mqEditingId, setMqEditingId] = useState(null);
  const [showMqForm, setShowMqForm] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragAppIdx, setDragAppIdx] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [a, p, m, c] = await Promise.all([
        api.get("/apps"),
        api.get("/payment-methods"),
        api.get("/marquee"),
        api.get("/contacts"),
      ]);
      setApps(a.data);
      setMethods(p.data);
      setMarquee(m.data);
      setContacts(c.data);
    } catch {
      /* handled per-action */
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => { setUser(data); loadAll(); })
      .catch(() => setUser(false));
  }, [loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      setUser(data);
      toast.success("Welcome back, cowboy.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  // ---------- Apps ----------

  const openNewApp = () => { setForm(emptyApp); setEditingId(null); setShowForm(true); };
  const openEditApp = (app) => {
    setForm({
      name: app.name, tagline: app.tagline || "", description: app.description || "",
      main_image: app.main_image || "", images: app.images || [], featured: !!app.featured,
      platforms: app.platforms || [], phone_frame: app.phone_frame !== false, slug: app.slug || "",
    });
    setEditingId(app.id);
    setShowForm(true);
  };

  const saveApp = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/apps/${editingId}`, { ...form, features: [] });
        toast.success("App updated");
      } else {
        await api.post("/apps", { ...form, features: [] });
        toast.success("App added to the stable");
      }
      setShowForm(false);
      const { data } = await api.get("/apps");
      setApps(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Delete this app? This cannot be undone.")) return;
    try {
      await api.delete(`/apps/${id}`);
      setApps(apps.filter((a) => a.id !== id));
      toast.success("App deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const moveImage = (idx, dir) => {
    const imgs = [...form.images];
    const j = idx + dir;
    if (j < 0 || j >= imgs.length) return;
    [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
    setForm({ ...form, images: imgs });
  };

  // ---------- Payment methods ----------

  const savePm = async (e) => {
    e.preventDefault();
    try {
      if (pmEditingId) {
        await api.put(`/payment-methods/${pmEditingId}`, pmForm);
        toast.success("Payment method updated");
      } else {
        await api.post("/payment-methods", pmForm);
        toast.success("Payment method added");
      }
      setShowPmForm(false);
      setPmForm({ label: "", logo: "" });
      setPmEditingId(null);
      const { data } = await api.get("/payment-methods");
      setMethods(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deletePm = async (id) => {
    if (!window.confirm("Delete this payment method?")) return;
    try {
      await api.delete(`/payment-methods/${id}`);
      setMethods(methods.filter((m) => m.id !== id));
      toast.success("Payment method deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---------- Marquee ----------

  const saveMq = async (e) => {
    e.preventDefault();
    try {
      if (mqEditingId) {
        await api.put(`/marquee/${mqEditingId}`, mqForm);
        toast.success("Marquee item updated");
      } else {
        await api.post("/marquee", mqForm);
        toast.success("Marquee item added");
      }
      setShowMqForm(false);
      setMqForm({ text: "" });
      setMqEditingId(null);
      const { data } = await api.get("/marquee");
      setMarquee(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteMq = async (id) => {
    if (!window.confirm("Remove this marquee item?")) return;
    try {
      await api.delete(`/marquee/${id}`);
      setMarquee(marquee.filter((m) => m.id !== id));
      toast.success("Marquee item removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dropMarquee = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const items = [...marquee];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(targetIdx, 0, moved);
    setDragIdx(null);
    setMarquee(items);
    try {
      const { data } = await api.put("/marquee/reorder", { ids: items.map((m) => m.id) });
      setMarquee(data);
      toast.success("Marquee order saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---------- Render ----------

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5500]" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm bg-[#12121A] border border-[#282836] rounded-3xl p-8">
          <img src={LOGO_URL} alt="Cowboy Apps" className="h-14 w-auto mx-auto mb-8" />
          <h1 className="font-display text-xl font-bold text-center mb-6">Admin login</h1>
          <div className="space-y-4">
            <input data-testid="admin-login-username-input" type="text" required autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" className={inputCls} />
            <input data-testid="admin-login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
            <button data-testid="admin-login-submit-button" type="submit" disabled={loginBusy} className="btn-brand w-full inline-flex items-center justify-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold py-3.5 rounded-xl disabled:opacity-60">
              {loginBusy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </div>
          <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-xs font-mono2 uppercase tracking-[0.2em] text-[#63637A] hover:text-white transition-colors">
            <ArrowLeft className="w-3 h-3" /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  const tabs = [
    { key: "apps", label: "Apps", icon: LayoutGrid },
    { key: "payments", label: "Payment Methods", icon: CreditCard },
    { key: "marquee", label: "Marquee", icon: MoveHorizontal },
    { key: "inquiries", label: `Inquiries (${contacts.length})`, icon: Inbox },
    { key: "analytics", label: "Analytics", icon: BarChart3 },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <header className="sticky top-0 z-40 bg-[#08080C]/85 backdrop-blur-xl border-b border-[#282836]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Cowboy Apps" className="h-9 w-auto" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#63637A]">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site" className="text-xs font-mono2 uppercase tracking-[0.15em] text-[#9E9EB3] hover:text-white transition-colors">
              View site
            </Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 border border-[#282836] hover:border-[#FF5500] text-[#9E9EB3] hover:text-white rounded-full px-4 py-2 text-xs transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-2 mb-10 flex-wrap">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                tab === key ? "bg-[#FF5500] text-[#08080C]" : "border border-[#282836] text-[#9E9EB3] hover:text-white hover:border-[#FF5500]"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "apps" && (
          <div data-testid="admin-apps-panel">
            {!showForm ? (
              <>
                <button data-testid="admin-add-app-button" onClick={openNewApp} className="btn-brand inline-flex items-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold px-6 py-3 rounded-full mb-8">
                  <Plus className="w-4 h-4" /> Add new app
                </button>
                <p className="text-[#63637A] text-sm mb-6">Drag apps by the grip handle to set the order they appear on the site — it saves automatically.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {apps.map((app, i) => (
                    <div
                      key={app.id}
                      data-testid={`admin-app-row-${app.id}`}
                      draggable
                      onDragStart={() => setDragAppIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnd={() => setDragAppIdx(null)}
                      onDrop={async () => {
                        if (dragAppIdx === null || dragAppIdx === i) { setDragAppIdx(null); return; }
                        const items = [...apps];
                        const [moved] = items.splice(dragAppIdx, 1);
                        items.splice(i, 0, moved);
                        setDragAppIdx(null);
                        setApps(items);
                        try {
                          const { data } = await api.put("/apps/reorder", { ids: items.map((a) => a.id) });
                          setApps(data);
                          toast.success("App order saved");
                        } catch (err) {
                          toast.error(formatApiError(err));
                        }
                      }}
                      className={`bg-[#12121A] border rounded-2xl overflow-hidden transition-colors ${
                        dragAppIdx === i ? "border-[#FF5500] opacity-60" : "border-[#282836]"
                      }`}
                    >
                      {app.main_image && (
                        <div className="aspect-video overflow-hidden">
                          <SmartImage src={imgSrc(app.main_image)} alt={app.name} />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-center gap-3">
                          <span data-testid={`app-drag-handle-${app.id}`} className="cursor-grab active:cursor-grabbing text-[#63637A] hover:text-[#FF5500] transition-colors">
                            <GripVertical className="w-5 h-5" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-display font-semibold text-lg truncate">{app.name}</h3>
                            <p className="text-sm text-[#63637A] mt-1 truncate">{app.tagline}</p>
                          </div>
                          {app.featured && (
                            <span data-testid={`app-featured-badge-${app.id}`} className="font-mono2 text-[9px] uppercase tracking-widest text-[#08080C] bg-[#FF5500] rounded-full px-2.5 py-1 shrink-0">Featured</span>
                          )}
                          <span className="font-mono2 text-[10px] text-[#63637A]">{String(i + 1).padStart(2, "0")}</span>
                        </div>
                        <p className="text-xs text-[#63637A] mt-2 font-mono2">{(app.images || []).length} gallery image{(app.images || []).length === 1 ? "" : "s"}</p>
                        <div className="flex gap-2 mt-5">
                          <button
                            data-testid={`app-copy-link-${app.id}`}
                            title="Copy share link (shows app preview in Telegram)"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(`${window.location.origin}/api/share/${app.slug || app.id}`);
                                toast.success("Share link copied — paste it in Telegram");
                              } catch {
                                toast.error("Couldn't copy");
                              }
                            }}
                            className="inline-flex items-center justify-center border border-[#282836] hover:border-[#FF5500] text-sm rounded-xl px-3 py-2.5 transition-colors"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                          </button>
                          <a
                            data-testid={`app-share-telegram-${app.id}`}
                            title="Share on Telegram"
                            href={`https://t.me/share/url?url=${encodeURIComponent(`${window.location.origin}/api/share/${app.slug || app.id}`)}&text=${encodeURIComponent(`Check out ${app.name} — rebranded with your logo by Cowboy Apps`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center border border-[#282836] hover:border-[#FF5500] text-sm rounded-xl px-3 py-2.5 transition-colors"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </a>
                          <button data-testid={`app-edit-button-${app.id}`} onClick={() => openEditApp(app)} className="flex-1 inline-flex items-center justify-center gap-2 border border-[#282836] hover:border-[#FF5500] text-sm rounded-xl py-2.5 transition-colors">
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button data-testid={`app-delete-button-${app.id}`} onClick={() => deleteApp(app.id)} className="inline-flex items-center justify-center gap-2 border border-[#282836] hover:border-red-500 hover:text-red-400 text-sm rounded-xl px-4 py-2.5 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <form data-testid="app-form" onSubmit={saveApp} className="max-w-2xl bg-[#12121A] border border-[#282836] rounded-3xl p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-display text-xl font-bold">{editingId ? "Edit app" : "Add new app"}</h2>
                  <button type="button" data-testid="app-form-cancel-button" onClick={() => setShowForm(false)} className="text-[#63637A] hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <input data-testid="app-form-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="App name *" className={inputCls} />
                  <input data-testid="app-form-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="Link slug — blank = auto from name (e.g. xp-player)" className={inputCls} />
                  <input data-testid="app-form-tagline-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Short tagline (e.g. Rebranded for resellers)" className={inputCls} />
                  <textarea data-testid="app-form-description-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Full description" className={`${inputCls} resize-none`} />
                  <label className={`flex items-center gap-3 cursor-pointer border rounded-xl px-4 py-3.5 transition-colors ${form.featured ? "border-[#FF5500] bg-[#FF5500]/5" : "border-[#282836] hover:border-[#FF5500]"}`}>
                    <input data-testid="app-form-featured-toggle" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-[#FF5500]" />
                    <span className="text-sm">Feature this app as the spotlight at the top of the gallery</span>
                  </label>

                  <label className={`flex items-center gap-3 cursor-pointer border rounded-xl px-4 py-3.5 transition-colors ${form.phone_frame ? "border-[#FF5500] bg-[#FF5500]/5" : "border-[#282836] hover:border-[#FF5500]"}`}>
                    <input data-testid="app-form-phone-frame-toggle" type="checkbox" checked={form.phone_frame} onChange={(e) => setForm({ ...form, phone_frame: e.target.checked })} className="w-4 h-4 accent-[#FF5500]" />
                    <span className="text-sm">Show vertical screenshots inside a phone frame</span>
                  </label>

                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#63637A] mb-3">Supported platforms</p>
                    <div className="flex gap-3 flex-wrap">
                      {["Android TV", "Android Mobile"].map((p) => (
                        <label key={p} className={`flex items-center gap-2.5 cursor-pointer border rounded-xl px-4 py-3 text-sm transition-colors ${form.platforms.includes(p) ? "border-[#FF5500] bg-[#FF5500]/5 text-white" : "border-[#282836] text-[#9E9EB3] hover:border-[#FF5500]"}`}>
                          <input
                            data-testid={`app-form-platform-${p.replace(/\s+/g, "-").toLowerCase()}`}
                            type="checkbox"
                            checked={form.platforms.includes(p)}
                            onChange={(e) => setForm({ ...form, platforms: e.target.checked ? [...form.platforms, p] : form.platforms.filter((x) => x !== p) })}
                            className="w-4 h-4 accent-[#FF5500]"
                          />
                          {p}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#63637A] mb-3">Main screenshot</p>
                    {form.main_image && (
                      <div className="relative w-48 aspect-video mb-3 rounded-xl overflow-hidden border border-[#282836]">
                        <img src={imgSrc(form.main_image)} alt="Main screenshot" className="w-full h-full object-cover" />
                        <button type="button" data-testid="app-form-main-image-remove" onClick={() => setForm({ ...form, main_image: "" })} className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center text-white hover:text-red-400">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <UploadButton testid="app-form-main-image-upload" label={form.main_image ? "Replace main screenshot" : "Upload main screenshot"} onUploaded={(p) => setForm((f) => ({ ...f, main_image: p }))} />
                  </div>

                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#63637A] mb-3">
                      Gallery images — use arrows to reorder
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {form.images.map((img, i) => (
                        <div key={img + i} data-testid={`app-form-gallery-item-${i}`} className="relative aspect-video rounded-xl overflow-hidden border border-[#282836] group">
                          <img src={imgSrc(img)} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                          <span className="absolute top-1.5 left-1.5 bg-black/70 rounded-md px-1.5 py-0.5 font-mono2 text-[10px] text-[#FF5500]">{i + 1}</span>
                          <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 p-1.5 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" data-testid={`gallery-move-left-${i}`} onClick={() => moveImage(i, -1)} disabled={i === 0} className="w-7 h-7 bg-[#08080C] border border-[#282836] rounded-lg flex items-center justify-center hover:border-[#FF5500] disabled:opacity-30">
                              <ArrowUp className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                            <button type="button" data-testid={`gallery-move-right-${i}`} onClick={() => moveImage(i, 1)} disabled={i === form.images.length - 1} className="w-7 h-7 bg-[#08080C] border border-[#282836] rounded-lg flex items-center justify-center hover:border-[#FF5500] disabled:opacity-30">
                              <ArrowDown className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                            <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })} className="w-7 h-7 bg-[#08080C] border border-[#282836] rounded-lg flex items-center justify-center hover:border-red-500 hover:text-red-400">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <UploadButton testid="app-form-gallery-upload" label="Add gallery images" onUploaded={(p) => setForm((f) => ({ ...f, images: [...f.images, p] }))} />
                  </div>

                  <button data-testid="app-form-submit-button" type="submit" disabled={saving} className="btn-brand w-full inline-flex items-center justify-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold py-3.5 rounded-xl disabled:opacity-60">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingId ? "Save changes" : "Add app"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {tab === "payments" && (
          <div data-testid="admin-payments-panel">
            {!showPmForm ? (
              <>
                <button data-testid="admin-add-payment-button" onClick={() => { setPmForm({ label: "", logo: "" }); setPmEditingId(null); setShowPmForm(true); }} className="btn-brand inline-flex items-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold px-6 py-3 rounded-full mb-8">
                  <Plus className="w-4 h-4" /> Add payment method
                </button>
                {methods.length === 0 ? (
                  <p data-testid="payments-empty-message" className="text-[#63637A] font-mono2 text-xs uppercase tracking-[0.2em] py-16 text-center">
                    No payment methods yet — add the ones you accept.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {methods.map((m) => (
                      <div key={m.id} data-testid={`admin-payment-row-${m.id}`} className="bg-[#12121A] border border-[#282836] rounded-2xl p-6 flex items-center gap-4">
                        {m.logo ? (
                          <img src={imgSrc(m.logo)} alt={m.label} className="h-10 w-16 object-contain rounded-lg bg-[#0C0C12] p-1.5" />
                        ) : (
                          <div className="h-10 w-16 rounded-lg bg-[#0C0C12] border border-dashed border-[#282836] flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-[#63637A]" />
                          </div>
                        )}
                        <span className="font-display font-semibold flex-1">{m.label}</span>
                        <button data-testid={`payment-edit-button-${m.id}`} onClick={() => { setPmForm({ label: m.label, logo: m.logo }); setPmEditingId(m.id); setShowPmForm(true); }} className="w-9 h-9 border border-[#282836] hover:border-[#FF5500] rounded-xl flex items-center justify-center transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`payment-delete-button-${m.id}`} onClick={() => deletePm(m.id)} className="w-9 h-9 border border-[#282836] hover:border-red-500 hover:text-red-400 rounded-xl flex items-center justify-center transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <form data-testid="payment-form" onSubmit={savePm} className="max-w-md bg-[#12121A] border border-[#282836] rounded-3xl p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-display text-xl font-bold">{pmEditingId ? "Edit payment method" : "Add payment method"}</h2>
                  <button type="button" data-testid="payment-form-cancel-button" onClick={() => setShowPmForm(false)} className="text-[#63637A] hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <input data-testid="payment-form-label-input" required value={pmForm.label} onChange={(e) => setPmForm({ ...pmForm, label: e.target.value })} placeholder="Label (e.g. Cash App, PayPal, Venmo) *" className={inputCls} />
                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#63637A] mb-3">Logo</p>
                    {pmForm.logo && (
                      <div className="relative w-32 h-16 mb-3 rounded-xl overflow-hidden border border-[#282836] bg-[#0C0C12]">
                        <img src={imgSrc(pmForm.logo)} alt="Payment logo" className="w-full h-full object-contain p-2" />
                        <button type="button" data-testid="payment-form-logo-remove" onClick={() => setPmForm({ ...pmForm, logo: "" })} className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center text-white hover:text-red-400">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <UploadButton testid="payment-form-logo-upload" label={pmForm.logo ? "Replace logo" : "Upload logo"} onUploaded={(p) => setPmForm((f) => ({ ...f, logo: p }))} />
                  </div>
                  <button data-testid="payment-form-submit-button" type="submit" className="btn-brand w-full bg-[#FF5500] text-[#08080C] font-semibold py-3.5 rounded-xl">
                    {pmEditingId ? "Save changes" : "Add payment method"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {tab === "marquee" && (
          <div data-testid="admin-marquee-panel">
            {!showMqForm ? (
              <>
                <button data-testid="admin-add-marquee-button" onClick={() => { setMqForm({ text: "" }); setMqEditingId(null); setShowMqForm(true); }} className="btn-brand inline-flex items-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold px-6 py-3 rounded-full mb-8">
                  <Plus className="w-4 h-4" /> Add marquee item
                </button>
                <p className="text-[#63637A] text-sm mb-6">Drag phrases by the grip handle to reorder them — the new order saves automatically. Delete them all and the strip disappears from the site.</p>
                {marquee.length === 0 ? (
                  <p data-testid="marquee-empty-message" className="text-[#63637A] font-mono2 text-xs uppercase tracking-[0.2em] py-16 text-center">
                    No marquee items — the scrolling strip is currently hidden on the site.
                  </p>
                ) : (
                  <div className="space-y-3 max-w-2xl">
                    {marquee.map((m, i) => (
                      <div
                        key={m.id}
                        data-testid={`admin-marquee-row-${m.id}`}
                        draggable
                        onDragStart={() => setDragIdx(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => dropMarquee(i)}
                        onDragEnd={() => setDragIdx(null)}
                        className={`bg-[#12121A] border rounded-2xl px-4 py-4 flex items-center gap-3 transition-colors ${
                          dragIdx === i ? "border-[#FF5500] opacity-60" : "border-[#282836]"
                        }`}
                      >
                        <span data-testid={`marquee-drag-handle-${m.id}`} className="cursor-grab active:cursor-grabbing text-[#63637A] hover:text-[#FF5500] transition-colors">
                          <GripVertical className="w-5 h-5" />
                        </span>
                        <span className="font-mono2 text-[10px] text-[#63637A] w-6">{String(i + 1).padStart(2, "0")}</span>
                        <span className="font-display font-semibold flex-1">{m.text}</span>
                        <button data-testid={`marquee-edit-button-${m.id}`} onClick={() => { setMqForm({ text: m.text }); setMqEditingId(m.id); setShowMqForm(true); }} className="w-9 h-9 border border-[#282836] hover:border-[#FF5500] rounded-xl flex items-center justify-center transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`marquee-delete-button-${m.id}`} onClick={() => deleteMq(m.id)} className="w-9 h-9 border border-[#282836] hover:border-red-500 hover:text-red-400 rounded-xl flex items-center justify-center transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <form data-testid="marquee-form" onSubmit={saveMq} className="max-w-md bg-[#12121A] border border-[#282836] rounded-3xl p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-display text-xl font-bold">{mqEditingId ? "Edit marquee item" : "Add marquee item"}</h2>
                  <button type="button" data-testid="marquee-form-cancel-button" onClick={() => setShowMqForm(false)} className="text-[#63637A] hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <input data-testid="marquee-form-text-input" required value={mqForm.text} onChange={(e) => setMqForm({ text: e.target.value })} placeholder="Scrolling phrase (e.g. REBRANDED FOR YOU) *" className={inputCls} />
                  <button data-testid="marquee-form-submit-button" type="submit" className="btn-brand w-full bg-[#FF5500] text-[#08080C] font-semibold py-3.5 rounded-xl">
                    {mqEditingId ? "Save changes" : "Add item"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {tab === "analytics" && <AnalyticsPanel />}

        {tab === "settings" && <SettingsPanel />}

        {tab === "inquiries" && (
          <div data-testid="admin-inquiries-panel">
            {contacts.length === 0 ? (
              <p data-testid="inquiries-empty-message" className="text-[#63637A] font-mono2 text-xs uppercase tracking-[0.2em] py-16 text-center">
                No inquiries yet — they'll land here and in your email.
              </p>
            ) : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} data-testid={`inquiry-row-${c.id}`} className="bg-[#12121A] border border-[#282836] rounded-2xl p-6">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                      <h3 className="font-display font-semibold">{c.name}</h3>
                      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#63637A]">
                        {new Date(c.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-[#FF9900]">{c.contact}</p>
                    {c.app_interest && <p className="text-sm text-[#9E9EB3] mt-1">Interested in: <span className="text-[#F4F4F8]">{c.app_interest}</span></p>}
                    <p className="text-sm text-[#9E9EB3] mt-3 whitespace-pre-wrap">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
